import {
  Alert as MuiAlert,
  Box,
  Button,
  CardContent,
  CircularProgress,
} from "@mui/material";
import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSubmitResultKey } from "../../api/Api";
import { isExpectedCardDecline } from "../../utils/paymentResultError";
import OrderConfirmationMessage from "./OrderConfirmationMessage";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCustomerPaymentInfo } from "./index";
import {
  OneSolomonAlert,
  OneSolomonContentPage,
  OneSolomonPageBackground,
  OneSolomonSpinner,
  resolveIsCompanyOneSolomons,
} from "./oneSolomonUi";

const Alert = styled(MuiAlert)(spacing);

const PaymentResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { values: customerInfo } = useCustomerPaymentInfo();
  const { ResultKey, transaction_for, ...otherParams } =
    Object.fromEntries(searchParams);

  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    customerInfo?.company_uuid,
    customerInfo?.isCompanyOneSolomons
  );

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutate, error, data, isError, isPending, isSuccess } =
    useSubmitResultKey();

  useEffect(() => {
    if (!executeRecaptcha) {
      return;
    }

    if (!ResultKey) {
      navigate("/");
      return;
    }

    executeRecaptcha("PaymentResult_submitResultKey").then(
      (recaptchaResponse) => {
        mutate({
          ResultKey,
          transaction_for: transaction_for ?? "deposit",
          "g-recaptcha-response": recaptchaResponse,
          merchant_reference: otherParams.merchant_reference,
          order_uuid: otherParams.order_uuid,
          transaction_uuid: otherParams.transaction_uuid,
        });
      }
    );
  }, [ResultKey, executeRecaptcha]);

  useEffect(() => {
    if (!error) {
      return;
    }

    // An expected card decline (e.g. BPoint ResponseCode "5" — not sufficient
    // funds) arrives here as a 400 from submit-result-key. It is not an
    // application fault: the decline message is already rendered below, and the
    // 400 stays observable in the RUM resource stream and pportal-api logs.
    // BUG-2026-11-05T010822 — keep the RUM error stream signal-only.
    if (isExpectedCardDecline(error)) {
      return;
    }

    console.error(error);
  }, [error]);

  const content = (
    <>
      {isPending &&
        (isCompanyOneSolomons ? (
          <OneSolomonSpinner />
        ) : (
          <Box display="flex" justifyContent="center" my={6}>
            <CircularProgress />
          </Box>
        ))}

      {isError &&
        (isCompanyOneSolomons ? (
          <OneSolomonAlert variant="error">
            {(error as any)?.response?.data?.message ?? "Something went wrong!"}
          </OneSolomonAlert>
        ) : (
          <Alert severity="error" my={3}>
            {(error as any)?.response?.data?.message ?? "Something went wrong!"}
          </Alert>
        ))}

      {isSuccess && (
        <>
          {String(data?.TxnResp?.ResponseCode) === "0" ? (
            <>
              {isCompanyOneSolomons ? (
                <OneSolomonAlert variant="success">
                  {`Payment Successful! Transaction Number: ${data?.TxnResp?.TxnNumber}, DateTime: ${data?.TxnResp?.ProcessedDateTime}`}
                </OneSolomonAlert>
              ) : (
                <Alert severity="success" my={3}>
                  {`Payment Successful! Transaction Number: ${data?.TxnResp?.TxnNumber}, DateTime: ${data?.TxnResp?.ProcessedDateTime}`}
                </Alert>
              )}

              <OrderConfirmationMessage />
            </>
          ) : (
            <>
              {isCompanyOneSolomons ? (
                <OneSolomonAlert variant="error">
                  Uh oh! Your transaction could not be processed.
                </OneSolomonAlert>
              ) : (
                <Alert severity="error" my={3}>
                  Uh oh! Your transaction could not be processed.
                </Alert>
              )}

              <Button
                onClick={() => navigate("/", { replace: true })}
                color="primary"
                variant="contained"
                fullWidth
                sx={{ mt: 3 }}
              >
                Please try again.
              </Button>
            </>
          )}
        </>
      )}
    </>
  );

  if (isCompanyOneSolomons) {
    return (
      <>
        <OneSolomonPageBackground />
        <OneSolomonContentPage>{content}</OneSolomonContentPage>
      </>
    );
  }

  return <CardContent>{content}</CardContent>;
};

export default PaymentResult;
